// @ts-nocheck
import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
/* Store Custom Code
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
*/
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@toolkit/Framework/Types/Button";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { ProductSettingsModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productCardProps } from "@store/Themes/common/props/productCard";
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
/* Store Custom Code - End */

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
    // Store Custom Code
    forCardView?: boolean;
    productContext?: ProductContextModel;
    productSettings?: ProductSettingsModel;
    showPrice?: boolean;
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: FlexWrapperPresentationProps;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
    // Store Custom Code
    priceWrapper?: InjectableCss;
    priceWrapperPDP?: InjectableCss;
    price?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    addToCartWrapper?: InjectableCss;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
        /* Store Custom Code - Start */
        extendedStyles: {
            content: {
                css: css`
                    flex-flow: nowrap;
                    align-items: flex-end;
                `,
            },
        },
        /* Store Custom Code - End */
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    flex: 1 1 50%;
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 auto;
                    align-self: flex-end;
                `,
            },
        },
    },
    // Store Custom Code
    priceWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            text-align: right;
        `,
    },
    priceWrapperPDP: productCardProps.spacingWrapper,
    price: productCardProps.price,
    quantityBreakPricing: productCardProps.quantityBreakPricing,
    addToCartWrapper: productCardProps.spacingWrapper,
};

const AddToCartOptionsAndButton: FC<AddToCartOptionsAndButtonProps> = ({
    extendedStyles,
    overridedStyles,
    sizeVariant = "small",
    buttonSizeVariant: baseButtonSizeVariant,
    showUnitOfMeasure = true,
    // Store Custom Code
    forCardView,
    productContext,
    productSettings,
    showPrice = true,
}) => {
    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);
        const itemCss = flexWrapperGapStyles(baseStyles.flexWrapper?.gap, baseStyles.flexWrapper?.verticalGap);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            addToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
            /* Store Custom Code - Start */
            priceWrapper: { css: itemCss },
            /* Store Custom Code - End */
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant]);

    const showProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.addToCartButton?.configurationCompleted,
        extendedStyles?.addToCartButton?.variantSelectionCompleted,
    );
    const showProductQuantityOrdered = useShowProductQuantityOrdered(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    /* Store Custom Code - Start */
    if (forCardView && !showProductAddToCartButton && productContext?.product.canShowPrice) {
        return (
            <StyledWrapper {...styles.priceWrapperPDP}>
                {productContext && productSettings && (
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                )}
                <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
            </StyledWrapper>
        );
    }
    /* Store Custom Code - End */

    if (!showProductAddToCartButton && !showProductQuantityOrdered && !showProductUnitOfMeasureSelect) {
        return null;
    }

    return (
        <>
            <FlexWrapper wrap={false} {...styles.flexWrapper}>
                {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
                {/* Store Custom Code - Start */}
                <ProductQuantityOrdered {...styles.quantityOrdered} isProductCard={forCardView} />
                {!forCardView && <ProductAddToCartButton {...styles.addToCartButton} />}
                {forCardView && productContext && productSettings && showPrice && (
                    <StyledWrapper {...styles.priceWrapper}>
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.price}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </StyledWrapper>
                )}
                {/* Store Custom Code - End */}
            </FlexWrapper>
            {/* Store Custom Code - Start */}
            {forCardView && (
                <StyledWrapper {...styles.addToCartWrapper}>
                    <ProductAddToCartButton {...styles.addToCartButton} />
                </StyledWrapper>
            )}
            {/* Store Custom Code - End */}
        </>
    );
};

export default AddToCartOptionsAndButton;
